import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { pageHeadScripts } from "@/lib/pageJsonLd";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { QuoteCTA } from "@/components/QuoteCTA";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import hero from "@/assets/original/hero-shade.jpg";
import g1 from "@/assets/original/gallery1.jpg";
import g2 from "@/assets/original/gallery2.jpg";
import g3 from "@/assets/original/gallery3.jpg";
import g4 from "@/assets/original/gallery4.jpg";
import g5 from "@/assets/original/gallery5.jpg";
import fab from "@/assets/original/fabrication.jpg";
import tension from "@/assets/original/tension.jpg";
import porch from "@/assets/original/stock-porch.jpg";
import comm from "@/assets/original/stock-commercial.jpg";
import trad from "@/assets/original/stock-traditional.jpg";
import curtains from "@/assets/original/stock-curtains.jpg";
import cushions from "@/assets/original/stock-cushions.jpg";
import { AUTO_PROJECT_PHOTOS } from "@/data/autoProjectPhotos";
import { MISTING_PHOTOS } from "@/data/mistingPhotos";

type Category =
  | "Awnings"
  | "Sail Shades"
  | "Covers"
  | "Curtains"
  | "Pergolas & Patios"
  | "Welding & Fabrication"
  | "Upholstery"
  | "Misting & Cooling";

type Location =
  | "Seguin"
  | "San Antonio"
  | "New Braunfels"
  | "Austin"
  | "Boerne"
  | "Kerrville"
  | "Houston";

type Photo = {
  src: string;
  alt: string;
  category: Category;
  location: Location;
};

const featured: Photo[] = [
  { src: hero, alt: "Custom shade structure over baseball field", category: "Sail Shades", location: "San Antonio" },
  { src: g1, alt: "Custom Texas Made Shade project", category: "Awnings", location: "Seguin" },
  { src: g2, alt: "Custom canvas and welding project", category: "Welding & Fabrication", location: "New Braunfels" },
  { src: g3, alt: "Custom shade installation", category: "Pergolas & Patios", location: "Austin" },
  { src: g4, alt: "Custom canvas project", category: "Covers", location: "Boerne" },
  { src: g5, alt: "Custom shade structure", category: "Sail Shades", location: "Kerrville" },
  { src: fab, alt: "Sunbrella fabric piece with star design and grommets", category: "Welding & Fabrication", location: "Seguin" },
  { src: tension, alt: "Blue shade sail tension structure over playground", category: "Sail Shades", location: "San Antonio" },
  { src: porch, alt: "Porch shade and retractable awning", category: "Pergolas & Patios", location: "New Braunfels" },
  { src: comm, alt: "Commercial awning installation", category: "Awnings", location: "Houston" },
  { src: trad, alt: "Traditional commercial awning", category: "Awnings", location: "Austin" },
  { src: curtains, alt: "Drop down curtains and retractable shade", category: "Curtains", location: "Boerne" },
  { src: cushions, alt: "Sunbrella upholstery cushions", category: "Upholstery", location: "Seguin" },
];

// Merge featured photos with every project image in src/assets/.
// De-dupe by src so we never show the same file twice.
const seen = new Set<string>();
const mistingGalleryPhotos: Photo[] = MISTING_PHOTOS.map((p) => ({
  src: p.src,
  alt: p.alt,
  category: "Misting & Cooling" as const,
  location: "Seguin" as const,
}));

const photos: Photo[] = [...featured, ...mistingGalleryPhotos, ...(AUTO_PROJECT_PHOTOS as readonly Photo[])].filter((p) => {
  if (seen.has(p.src)) return false;
  seen.add(p.src);
  return true;
});

const ALL = "All" as const;

const PAGE_SIZE_OPTIONS = [12, 24, 48] as const;
const DEFAULT_PAGE_SIZE = 12;

const projectsSearchSchema = z.object({
  page: fallback(z.number().int().min(1).optional(), undefined),
  pageSize: fallback(z.number().int().refine((n) => (PAGE_SIZE_OPTIONS as readonly number[]).includes(n)).optional(), undefined),
});

function ProjectGallery() {
  const search = Route.useSearch();
  const page = search.page ?? 1;
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE;
  const navigate = useNavigate({ from: "/projects" });

  const [category, setCategory] = useState<Category | typeof ALL>(ALL);
  const [query, setQuery] = useState("");

  const categories = useMemo(
    () => [ALL, ...Array.from(new Set(photos.map((p) => p.category)))] as const,
    [],
  );

  const q = query.trim().toLowerCase();
  const filtered = photos.filter((p) => {
    if (category !== ALL && p.category !== category) return false;
    if (q) {
      const haystack = `${p.alt} ${p.category}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const pageItems = filtered.slice(startIdx, startIdx + pageSize);

  // Reset to page 1 whenever the filters change.
  useEffect(() => {
    if (page !== 1) {
      navigate({ search: (prev) => ({ ...prev, page: undefined }), replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, q]);

  // If page is out of range (e.g. filter shrinks results), snap back.
  useEffect(() => {
    if (page > totalPages) {
      navigate({ search: (prev) => ({ ...prev, page: totalPages === 1 ? undefined : totalPages }), replace: true });
    }
  }, [page, totalPages, navigate]);

  const goToPage = (next: number) => {
    const clamped = Math.min(Math.max(1, next), totalPages);
    navigate({ search: (prev) => ({ ...prev, page: clamped === 1 ? undefined : clamped }) });
    if (typeof window !== "undefined") {
      document.getElementById("project-gallery-heading")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const setPageSize = (size: number) => {
    navigate({ search: (prev) => ({
      ...prev,
      pageSize: size === DEFAULT_PAGE_SIZE ? undefined : size,
      page: undefined,
    }) });
  };

  const chip = (active: boolean) =>
    `inline-flex items-center px-4 py-2 font-display text-xs uppercase tracking-wider transition border-2 ${
      active
        ? "bg-navy-deep text-white border-navy-deep"
        : "bg-white text-navy-deep border-navy-deep/20 hover:border-navy-deep"
    }`;

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        <section className="relative bg-navy-deep py-20 text-white">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="font-display text-sm tracking-[0.3em] text-white">★ PROJECTS</div>
            <h1 className="mt-3 font-display text-5xl font-bold md:text-7xl">Real Projects. Real Texas Shade.</h1>
            <p className="mt-4 max-w-2xl text-lg text-white/80">A gallery of custom awnings, shade structures, covers, curtains, and welded fabrication built in our Seguin shop and installed across Texas.</p>
          </div>
        </section>
        <section className="bg-cream py-16" aria-labelledby="project-gallery-heading">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <h2 id="project-gallery-heading" className="sr-only">
              Project Gallery — Custom Awnings, Covers &amp; Shade Across Texas
            </h2>

            {/* FILTERS */}
            <div className="mb-8 space-y-5 border-l-4 border-red bg-white p-5 shadow-sm">
              <div>
                <div className="mb-2 font-display text-xs uppercase tracking-[0.25em] text-navy-deep/70">
                  Filter by project type
                </div>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by project type">
                  {categories.map((c) => (
                    <button key={c} type="button" onClick={() => setCategory(c)} className={chip(category === c)} aria-pressed={category === c}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-3 pt-1 text-sm text-navy-deep/70 sm:flex-row sm:items-center sm:justify-between">
                <span>
                  Showing{" "}
                  <strong className="text-navy-deep">
                    {filtered.length === 0 ? 0 : startIdx + 1}
                    {filtered.length > pageItems.length ? `–${startIdx + pageItems.length}` : ""}
                  </strong>{" "}
                  of <strong className="text-navy-deep">{filtered.length}</strong>
                  {filtered.length !== photos.length && <> (filtered from {photos.length})</>}
                </span>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 font-display text-xs uppercase tracking-[0.2em] text-navy-deep/70">
                    Per page
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="border-2 border-navy-deep/20 bg-white px-2 py-1 text-xs text-navy-deep focus:border-navy-deep focus:outline-none"
                      aria-label="Projects per page"
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </label>
                  {(category !== ALL || query) && (
                    <button
                      type="button"
                      onClick={() => { setCategory(ALL); setQuery(""); }}
                      className="font-display text-xs uppercase tracking-wider text-red underline-offset-2 hover:underline"
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              </div>
            </div>

            {filtered.length > 0 ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {pageItems.map((p) => (
                    <div key={p.src} className="group relative aspect-[4/3] overflow-hidden bg-navy-deep">
                      <img src={p.src} alt={p.alt} loading="lazy" decoding="async" width={800} height={600} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex flex-wrap gap-1.5 bg-gradient-to-t from-navy-deep/85 via-navy-deep/40 to-transparent p-3 opacity-0 transition group-hover:opacity-100">
                        <span className="bg-red px-2 py-0.5 font-display text-[10px] uppercase tracking-wider text-white">{p.category}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <nav aria-label="Project gallery pagination" className="mt-10 flex flex-wrap items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => goToPage(safePage - 1)}
                      disabled={safePage <= 1}
                      className="inline-flex items-center px-4 py-2 font-display text-xs uppercase tracking-wider border-2 border-navy-deep/20 bg-white text-navy-deep transition hover:border-navy-deep disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-navy-deep/20"
                      aria-label="Previous page"
                    >
                      ← Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => {
                      const active = n === safePage;
                      return (
                        <button
                          key={n}
                          type="button"
                          onClick={() => goToPage(n)}
                          aria-current={active ? "page" : undefined}
                          aria-label={`Go to page ${n}`}
                          className={`inline-flex h-10 min-w-[2.5rem] items-center justify-center px-3 font-display text-xs uppercase tracking-wider border-2 transition ${
                            active
                              ? "bg-navy-deep text-white border-navy-deep"
                              : "bg-white text-navy-deep border-navy-deep/20 hover:border-navy-deep"
                          }`}
                        >
                          {n}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => goToPage(safePage + 1)}
                      disabled={safePage >= totalPages}
                      className="inline-flex items-center px-4 py-2 font-display text-xs uppercase tracking-wider border-2 border-navy-deep/20 bg-white text-navy-deep transition hover:border-navy-deep disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-navy-deep/20"
                      aria-label="Next page"
                    >
                      Next →
                    </button>
                  </nav>
                )}
              </>
            ) : (
              <div className="border-2 border-dashed border-navy-deep/20 bg-white p-12 text-center">
                <p className="font-display text-lg text-navy-deep">No projects match these filters yet.</p>
                <p className="mt-2 text-sm text-navy-deep/70">Try clearing a filter — or call +1 (830) 390-9588 to be one of the first.</p>
              </div>
            )}
          </div>
        </section>
        <QuoteCTA />
        <LetsTalkForm />
      </main>
      <SiteFooter />
    </div>
  );
}

export const Route = createFileRoute("/projects")({
  validateSearch: zodValidator(projectsSearchSchema),
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Texas Project Gallery — Awnings, Shades & Covers",
      description: "Real Texas projects: custom awnings, sail shades, boat & trailer covers, pergolas, curtains, upholstery, and welded fabrication built in Seguin, TX.",
      image: g1,
      imageAlt: "Texas Made Shade project gallery — custom awnings, sail shades, and covers",
      path: "/projects",
      socialTitle: "Project Gallery | Texas Made Shade & Covers",
      socialDescription: "Custom awnings, sail shades, covers, pergolas, curtains and welded fabrication across Texas.",
      keywords: "Texas awnings, sail shades, boat covers, trailer covers, pergola covers, custom canvas, welded fabrication, Seguin TX, San Antonio, New Braunfels, Austin",
    }),
    scripts: pageHeadScripts({
      path: "/projects",
      name: "Texas Project Gallery — Awnings, Shades & Covers",
      description: "Real Texas projects: custom awnings, sail shades, boat & trailer covers, pergolas, curtains, upholstery and welded fabrication built in Seguin, TX.",
    }),
  }),
  component: ProjectGallery,
});
